import type {
  WorkbookDefinition,
  WorkbookColumnarSheet,
  WorkbookTable,
} from '@erp/shared-types';
import {
  columnReference,
  suggestColumnType,
  type ColumnType,
  type WorkbookPlan,
} from '@erp/workbooks';

type Lookup = WorkbookColumnarSheet['lookups'][number];

export function hasColumnFormula(
  column: WorkbookColumnarSheet['columns'][number],
) {
  return column.expression.trim() !== columnReference(column.name);
}

/**
 * Whether an input column is the Field a Worksheet's filters read by that
 * name: yes unless an output column of the same name computes something else.
 */
export function inputColumnIsField(sheet: WorkbookColumnarSheet, name: string) {
  const column = sheet.columns.find((item) => item.name === name);
  return !column || !hasColumnFormula(column);
}

export function moveWorksheetColumn(
  sheet: WorkbookColumnarSheet,
  index: number,
  delta: number,
) {
  const target = index + delta;
  if (target < 0 || target >= sheet.columns.length) return sheet;
  const columns = [...sheet.columns];
  const column = columns.splice(index, 1)[0];
  if (!column) return sheet;
  columns.splice(target, 0, column);
  return { ...sheet, columns };
}

/**
 * The Worksheet with its columns in `order`, each entry a column's current
 * index. An order that isn't a permutation of the columns leaves it as is.
 */
export function reorderWorksheetColumns(
  sheet: WorkbookColumnarSheet,
  order: readonly number[],
) {
  const columns = order.flatMap((from) => sheet.columns[from] ?? []);
  if (
    columns.length !== sheet.columns.length ||
    new Set(order).size !== order.length ||
    order.every((from, to) => from === to)
  )
    return sheet;
  return { ...sheet, columns };
}

export function removeWorksheetColumn(
  sheet: WorkbookColumnarSheet,
  index: number,
) {
  const column = sheet.columns[index];
  if (!column || sheet.columns.length === 1) return sheet;
  return {
    ...sheet,
    columns: sheet.columns.filter((_, i) => i !== index),
    filters: sheet.filters.filter((filter) => filter.column !== column.name),
    sort: sheet.sort.filter((sort) => sort.column !== column.name),
  };
}

export function nextColumnName(
  base: string,
  columns: WorkbookColumnarSheet['columns'],
) {
  const names = new Set(columns.map((column) => column.name.toLowerCase()));
  let name = base;
  let suffix = 2;
  while (names.has(name.toLowerCase())) name = `${base} ${suffix++}`;
  return name;
}

export function worksheetColumnLetter(index: number): string {
  let label = '';
  for (let value = index + 1; value > 0; value = Math.floor((value - 1) / 26)) {
    label = String.fromCharCode(65 + ((value - 1) % 26)) + label;
  }
  return label;
}

/**
 * The type a Lookup's output takes: its value column's Declared type when the
 * Lookup reads a Worksheet, text when it reads a Source.
 */
export function lookupValueType(
  plan: WorkbookPlan,
  lookup: Lookup,
): ColumnType {
  return (
    plan.sheets
      .get(lookup.sourceId)
      ?.output.find((field) => field.name === lookup.valueColumn)?.type ??
    'text'
  );
}

/**
 * Default types for columns Add Column creates from Fields: a Worksheet input's
 * Declared types and Lookup output types, as planned; for a Source input, a
 * Suggested type guessed from its preview values. The guess never reaches the
 * engine: Source columns are text there.
 */
export function addColumnDefaultTypes(
  sheet: WorkbookColumnarSheet,
  definition: WorkbookDefinition,
  plan: WorkbookPlan,
  input: WorkbookTable | undefined,
): Map<string, ColumnType> {
  const types = new Map(
    sheet.lookups.map((lookup) => [
      lookup.outputColumn,
      lookupValueType(plan, lookup),
    ]),
  );
  if (definition.sources.some((source) => source.id === sheet.inputId))
    input?.columns.forEach((name, index) =>
      types.set(
        name,
        suggestColumnType(input.rows.map((row) => row[index] ?? null)),
      ),
    );
  else
    for (const field of plan.sheets.get(sheet.inputId)?.output ?? [])
      types.set(field.name, field.type);
  return types;
}
